import type Big from 'big.js'
import { type NotificationActionTypes, updateTransactionToast } from 'components'
import { type CurrencyMetadata, getProductByAddress } from 'indices-list'
import type { ChainId } from 'sdk'
import { chainMappings } from 'sdk/omni/omni-transaction-service/constants'
import type { OmniTransactionService } from 'sdk/omni/omni-transaction-service/service'
import { TransactionStatus } from 'sdk/omni/omni-transaction-service/types'
import type { BlockInfo } from 'services/block-updater'
import { viemPublicClients } from 'services/wallet/chains'
import { type Address, type TransactionReceipt, decodeEventLog, parseAbi } from 'viem'
import { safeWaitForTransactionReceipt } from '@moleculexyz/wagmi-safe-wait-for-tx'

export type TransactionAction = NotificationActionTypes

export type TransactionChain = 'eth'

export type IndexActionDetails = {
  indexTicker: string
  assetName: string
  assetDecimals: number
  value: string
}

export type StakingActionDetails = {
  assetName: string
  assetDecimals: number
  value: string
}

export type TransactionActionDetails = {
  action: TransactionAction
  indexDetails: IndexActionDetails | null
  stakingDetails: StakingActionDetails | null
  network: ChainId
}

export interface OmniTransactionStatuses {
  chain: number
  status: TransactionStatus
}

export interface Transaction {
  account: Address
  status: TransactionStatus
  hash: string
  replacementHash?: string
  isRejected?: boolean
  chainId: ChainId
  chain: TransactionChain
  addedTime: number
  isSingleChain: boolean
  isUserContract: boolean
  lastCheckedBlockNumber?: number
  receipt?: TransactionReceipt
  action: TransactionActionDetails
  additionalTransactions?: OmniTransactionStatuses[]
  additionalChains?: number[]
}

export const parseReceiptStatus = (status: 'success' | 'reverted') =>
  status === 'success'
    ? TransactionStatus.DELIVERED
    : status === 'reverted'
      ? TransactionStatus.FAILED
      : TransactionStatus.INFLIGHT

export const bySingleChain = (isSingle: boolean) => (tx: Transaction) =>
  tx.isSingleChain === isSingle

export const isPending = (tx: Transaction) => tx.status === TransactionStatus.INFLIGHT
export const isNotPending = (tx: Transaction) => tx.status !== TransactionStatus.INFLIGHT

export function trackTransaction(
  account: Address,
  hash: string,
  action: TransactionActionDetails,
  chainId: ChainId,
  isUserContract: boolean,
  isSingleChain?: boolean,
  omniChains?: number[],
): Transaction {
  return {
    account: account,
    hash: hash,
    status: TransactionStatus.INFLIGHT,
    chainId: chainId,
    isUserContract,
    chain: 'eth',
    addedTime: new Date().getTime(),
    action: action,
    isSingleChain: isSingleChain ?? true,
    additionalChains: omniChains,
  }
}

export const updateSingleChainTransaction = async (
  transaction: Transaction,
  isDarkMode: boolean,
  blockNumberInfo: BlockInfo,
): Promise<Transaction> => {
  const { chainId, hash, account } = transaction
  const activeChainIndex = blockNumberInfo.data.eth.findIndex(
    (el: { chainId: number; blockNumber: number }) =>
      typeof el.chainId === 'string' ? el.chainId : `0x${el.chainId}` === `0x${chainId}`,
  )
  if (!blockNumberInfo.data.eth || activeChainIndex < 0) {
    return transaction
  }
  const receipt = await safeWaitForTransactionReceipt(viemPublicClients[chainId], {
    hash: hash as Address,
    address: account,
    onReplaced: (replacement) => console.log('replacement params: ', replacement),
  })
  if (receipt) {
    transaction.status = parseReceiptStatus(receipt.status)
    transaction.replacementHash = receipt.transactionHash
  }
  return transaction
}

const withdrawEvent =
  'event Withdraw(address indexed sender, address indexed receiver, address indexed owner, uint256 shares, uint256 k, uint256 reserve)'

export const updateCrossChainTransaction = async (
  transaction: Transaction,
  isDarkMode: boolean,
  transactionClient: OmniTransactionService,
): Promise<Transaction> => {
  const { chainId, hash, replacementHash, additionalChains, action } = transaction
  if (!replacementHash) {
    const multisigTx = await safeWaitForTransactionReceipt(viemPublicClients[chainId], {
      hash: hash as Address,
      address: transaction.account,
    })

    if (multisigTx) {
      transaction.replacementHash = multisigTx.transactionHash
      transaction.hash === multisigTx.transactionHash
    }
  }

  const homeTx = await viemPublicClients[chainId].getTransactionReceipt({
    hash: hash as Address,
  })

  const getOmniTxStatus = async () => {
    const homeStatus: OmniTransactionStatuses = {
      chain: chainMappings[chainId],
      status: parseReceiptStatus(homeTx.status),
    }

    if (additionalChains && transaction.status === TransactionStatus.INFLIGHT) {
      const { transactions: omniTransactionList } =
        await transactionClient.getRemoteTransactionStatuses(hash || '0x0', additionalChains)

      const transactionsStatuses: OmniTransactionStatuses[] = omniTransactionList.map(
        ({ chain, statuses, escrowSwapsSuccess }) => ({
          chain,
          status:
            statuses.every((status) => status === TransactionStatus.DELIVERED) ||
            (statuses.some((status) => status === TransactionStatus.DELIVERED) &&
              !escrowSwapsSuccess)
              ? TransactionStatus.DELIVERED
              : statuses.some((status) => status === TransactionStatus.FAILED)
                ? TransactionStatus.FAILED
                : TransactionStatus.INFLIGHT,
          escrowSwapsSuccess,
        }),
      )
      const crossChainStatus = transactionsStatuses.every(
        ({ status }) => status === TransactionStatus.DELIVERED,
      )
        ? TransactionStatus.DELIVERED
        : transactionsStatuses.some(({ status }) => status === TransactionStatus.FAILED)
          ? TransactionStatus.FAILED
          : TransactionStatus.INFLIGHT

      transaction.additionalTransactions = [homeStatus, ...transactionsStatuses]
      if (crossChainStatus !== TransactionStatus.INFLIGHT) {
        transaction.status = crossChainStatus
        updateTransactionToast(transaction, isDarkMode)
      }
    }
    return transaction
  }

  const targetLog = homeTx.logs.find(
    (log) => log.topics[0] === '0xbbbdee62287b5bf3bee13cab60a29ad729cf38109bccbd2a986a11c99b8ca704',
  )
  if (action.action === 'retry') {
    return getOmniTxStatus()
  }
  if (targetLog) {
    const decodedLog = decodeEventLog({
      abi: parseAbi([withdrawEvent]),
      data: targetLog.data,
      topics: targetLog.topics,
    })

    if (decodedLog.args.k === 0n) {
      transaction.status = parseReceiptStatus(homeTx.status)
      return transaction
    }
    return getOmniTxStatus()
  }
  transaction.status = parseReceiptStatus(homeTx.status)
  return transaction
}

export const createApproveTransactionalAction = ({
  action,
  token,
  amount,
}: {
  action: TransactionAction
  token: CurrencyMetadata
  amount: Big
}): TransactionActionDetails => {
  return {
    action,
    stakingDetails: {
      assetName: token.symbol,
      assetDecimals: token.decimals,
      value: amount.toString(),
    },
    indexDetails: null,
    network: token.chainId,
  }
}

export const createV1TransactionalAction = ({
  action,
  inputToken,
  outputToken,
  amount,
  productAddress,
}: {
  action: TransactionAction
  amount: Big
  inputToken: CurrencyMetadata
  outputToken: CurrencyMetadata
  productAddress: Address
}): TransactionActionDetails => {
  const asset = inputToken.address === productAddress ? outputToken : inputToken
  return {
    action,
    stakingDetails: null,
    indexDetails: {
      indexTicker: getProductByAddress(productAddress).symbol.toUpperCase(),
      assetName: asset.symbol,
      assetDecimals: asset.decimals,
      value: amount.toString(),
    },
    network: inputToken.chainId,
  }
}
